<template>
	<div>
		<VueDatePicker
			:key="datePickerRenderKey"
			:range="{ partialRange: false }"
			:multi-calendars="{}"
			:preset-dates="presetRanges"
			v-bind="$attrs"
			:locale="dateFnsLocale || undefined"
			:action-row="{showSelect: false, showCancel: false}"
			auto-apply
			:time-config="{ enableTimePicker: false }"
			position="left"
			:formats="{ input: datePickerDateFormat }"
		>
			<template #dp-input="{ value, onInput, onEnter, onTab, onClear, onBlur, onKeypress, onPaste, isMenuOpen }">
				<div class="flex items-center">
					<CalendarDaysIcon class="w-5 h-5 absolute top-1 left-2 text-base-content-light z-10" />
					<input type="text"
						class="input input-sm w-full pl-10"
						:value="value" :placeholder="$t('Date range')"
						:readonly="$attrs['readonly']"
						:disabled="$attrs['disabled']"
					/>
				</div>
			</template>

			<template #preset-date-range-button="{ label, value, presetDate }">
				<button type="button" class="btn btn-sm btn-ghost normal-case whitespace-nowrap justify-start hover:bg-accent"
					:tabindex="0"
					@click="presetDate(value)"
					@keyup.enter.prevent="presetDate(value)"
				>
					{{ label }}
				</button>
			</template>
		</VueDatePicker>
	</div>
</template>


<style>
:root {
	--dp-border-radius: 0.375rem;
}

.dp--theme-light {
	--dp-primary-color: #6366f1;
	--dp-range-between-dates-background-color: #e0e7ff;
	--dp-range-between-border-color: #e0e7ff;
}

.dp--preset-dates {
	@apply sm:pt-2 flex sm:flex-col sm:gap-1;
}

.dp--outer-menu-wrap {
	@apply shadow-md;
}
</style>


<script lang="ts">
import { VueDatePicker } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { CalendarDaysIcon } from '@heroicons/vue/24/outline';
import { DateTime } from 'luxon';
import {mapStores} from "pinia";
import {useOrganisationsStore} from "@/stores/Organisations.store";
import type { Locale } from 'date-fns';
import { getDateFnsLocale } from '@/i18n.ts';

export default {
	inheritAttrs: false,

	components: {
		VueDatePicker,
		CalendarDaysIcon,
  	},

	props: {
		showLastSchoolYearPreset: {
			type: Boolean,
			default: false,
		}
	},

	data() {
		return {
			dateFnsLocale: null as Locale | null,
			datePickerRenderKey: 0,
		};
	},

	computed: {
		...mapStores(useOrganisationsStore),

		datePickerDateFormat() {
			const dateFormat = { year: "2-digit", month: "short", day: "2-digit" };
			return (dateRange) => {
				const localeCode = this.dateFnsLocale?.code || 'en-GB'; // To prevent mounting errors, initialise with 'en-GB' if the date-fns locale isn't loaded yet
				return [
					dateRange[0]?.toLocaleDateString(localeCode, dateFormat),
					dateRange[1]?.toLocaleDateString(localeCode, dateFormat)
				].join(' - ');
			}
		},

		presetRanges() {
			const today = DateTime.local();
			const yesterday = today.minus({days: 1});
			const lastMonday = today.minus({days: today.weekday - 1});
			const firstDayPreviousWeek = lastMonday.minus({days: 7});
			const lastDayPreviousWeek = lastMonday.minus({days: 1});
			const firstDayThisMonth = DateTime.local(today.year, today.month, 1);
			const firstDayPreviousMonth = today.minus({ months: 1 }).startOf('month');
			const lastDayPreviousMonth = firstDayThisMonth.minus({ days: 1 });

			const toRange = (a, b) => [a.toJSDate(), b.toJSDate()];

			const organisation = this.organisationsStore.currentOrganisation;

			const presetRanges = [
				{ label: this.$t('Today'), value: toRange(today, today), slot: 'preset-date-range-button' },
				{ label: this.$t('Yesterday'), value: toRange(yesterday, yesterday), slot: 'preset-date-range-button' },
				{ label: this.$t('This week'), value: toRange(lastMonday, today), slot: 'preset-date-range-button' },
				{ label: this.$t('Last week'), value: toRange(firstDayPreviousWeek, lastDayPreviousWeek), slot: 'preset-date-range-button' },
				{ label: this.$t('This month'), value: toRange(firstDayThisMonth, today), slot: 'preset-date-range-button' },
				{ label: this.$t('Last month'), value: toRange(firstDayPreviousMonth, lastDayPreviousMonth), slot: 'preset-date-range-button' }
			];

			if (organisation) {
				const schoolYearInterval = organisation.getCurrentSchoolYearInterval();
				if (!schoolYearInterval || !schoolYearInterval.start || !schoolYearInterval.end) {
					return presetRanges;
				}

				presetRanges.push({
					label: this.$t('This school year'),
					value: toRange(schoolYearInterval.start, schoolYearInterval.end),
					slot: 'preset-date-range-button'
				});

				if (this.showLastSchoolYearPreset) {
					presetRanges.push({
						label: this.$t('Last school year'),
						value: toRange(
							schoolYearInterval.start.minus({ years: 1 }),
							schoolYearInterval.end.minus({ years: 1 })
						),
						slot: 'preset-date-range-button'
					});
				}
			}

			return presetRanges;
		}
	},

	methods: {
		async loadDateFnsLocale() {
			this.dateFnsLocale = await getDateFnsLocale(this.$i18n.locale);
			// Targeted remount strategy: when locale finishes loading, the picker gets a new key so it rebuilds with the correct
			// formatter/locale instead of keeping initial cached (default) formatting.
			this.datePickerRenderKey += 1;
		},
	},

	async mounted() {
		await this.loadDateFnsLocale();
	},
}
</script>
